import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit'

export type ToastTone = 'success' | 'error' | 'info'
export type Theme = 'light' | 'dark'

export interface Toast {
  id: string
  tone: ToastTone
  message: string
}

export interface UiState {
  theme: Theme
  sidebarOpen: boolean
  toasts: Toast[]
}

function initialTheme(): Theme {
  try {
    const stored = localStorage.getItem('admin.theme')
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    /* ignore */
  }
  return 'dark'
}

const initialState: UiState = {
  theme: initialTheme(),
  sidebarOpen: false,
  toasts: []
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    themeToggled(state) {
      state.theme = state.theme === 'dark' ? 'light' : 'dark'
      try {
        localStorage.setItem('admin.theme', state.theme)
      } catch {
        /* ignore */
      }
    },
    sidebarToggled(state, action: PayloadAction<boolean | undefined>) {
      state.sidebarOpen = action.payload ?? !state.sidebarOpen
    },
    toastPushed: {
      reducer(state, action: PayloadAction<Toast>) {
        state.toasts.push(action.payload)
        // Keep the stack short enough to stay readable in a corner.
        if (state.toasts.length > 4) state.toasts.shift()
      },
      prepare(message: string, tone: ToastTone = 'success') {
        return { payload: { id: nanoid(), message, tone } }
      }
    },
    toastDismissed(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload)
    }
  }
})

export const { themeToggled, sidebarToggled, toastPushed, toastDismissed } = uiSlice.actions
export default uiSlice.reducer
